/**
 * Hook centralizado para notificações sonoras
 * Suporte cross-browser com AudioContext + fallback
 */

import { useRef, useCallback } from 'react';
import { useSoundPreferences } from './useSoundPreferences';

export type SoundEventType = 
  | 'new_order'       // Novo pedido pendente
  | 'urgent'          // Pedido urgente
  | 'delayed'         // Atraso detectado
  | 'completed'       // Step/pedido concluído
  | 'ready_pickup'    // Pronto para coleta
  | 'logistics_alert' // Alerta logístico
  | 'chat_message'    // Mensagem de chat interno
  | 'scan_ok'         // Bipagem aceita (posto de estoque)
  | 'scan_error'      // Bipagem recusada (posto de estoque)
  | 'error';          // Erro geral

interface SoundConfig {
  frequency: number;
  duration: number;
  pattern: number[];  // [on, off, on, off...] em ms
  type: OscillatorType;
}

// Configuração de sons por tipo de evento
const SOUND_CONFIGS: Record<SoundEventType, SoundConfig> = {
  new_order: {
    frequency: 800,
    duration: 200,
    pattern: [200],
    type: 'sine'
  },
  urgent: {
    frequency: 1000,
    duration: 100,
    pattern: [100, 80, 100, 80, 100],
    type: 'sine'
  },
  delayed: {
    frequency: 600,
    duration: 300,
    pattern: [300],
    type: 'triangle'
  },
  completed: {
    frequency: 1200,
    duration: 100,
    pattern: [100],
    type: 'sine'
  },
  ready_pickup: {
    frequency: 900,
    duration: 150,
    pattern: [150, 100, 150],
    type: 'sine'
  },
  logistics_alert: {
    frequency: 700,
    duration: 200,
    pattern: [200, 150, 200],
    type: 'triangle'
  },
  chat_message: {
    // Dois toques curtos ascendentes — distinto do new_order (tom único),
    // que o chat usava indevidamente e confundia a operação
    frequency: 950,
    duration: 80,
    pattern: [80, 60, 80],
    type: 'sine'
  },
  // ⚠️ Bipagem é REPETITIVA: 20 leituras seguidas numa separação. O som tem de
  // ser curto e discreto — o `completed` de 100ms já é longo demais quando toca
  // vinte vezes em dois minutos, e um som cansativo é desligado no primeiro dia.
  scan_ok: {
    frequency: 1400,
    duration: 45,
    pattern: [45],
    type: 'sine'
  },
  // O de erro é mais grave e mais longo DE PROPÓSITO: o operador está olhando
  // para a pilha, não para a tela, e precisa distinguir os dois sem erguer a
  // cabeça. Ainda assim curto — som de erro longo faz o posto inteiro parar.
  scan_error: {
    frequency: 320,
    duration: 220,
    pattern: [220],
    type: 'square'
  },
  error: {
    frequency: 400,
    duration: 500,
    pattern: [500],
    type: 'sawtooth'
  }
};

// Labels para UI
export const SOUND_EVENT_LABELS: Record<SoundEventType, string> = {
  new_order: 'Novo pedido pendente',
  urgent: 'Pedido urgente',
  delayed: 'Atraso detectado',
  completed: 'Etapa concluída',
  ready_pickup: 'Pronto para coleta',
  logistics_alert: 'Alerta logístico',
  chat_message: 'Mensagem de chat',
  scan_ok: 'Bipagem aceita (estoque)',
  scan_error: 'Bipagem recusada (estoque)',
  error: 'Erro/Problema'
};

export const useNotificationSound = () => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const { preferences } = useSoundPreferences();

  // Criar ou obter AudioContext
  const getAudioContext = useCallback((): AudioContext | null => {
    if (audioContextRef.current) {
      return audioContextRef.current;
    }

    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) {
        console.log('AudioContext não suportado neste navegador');
        return null;
      }
      audioContextRef.current = new AudioContextClass();
      return audioContextRef.current;
    } catch (err) {
      console.error('Erro ao criar AudioContext:', err);
      return null;
    }
  }, []);

  // Tocar um beep com configuração específica
  const playBeep = useCallback(async (
    ctx: AudioContext,
    frequency: number,
    duration: number,
    type: OscillatorType,
    volume: number,
    startTime: number
  ) => {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;
    gainNode.gain.value = volume * 0.3; // Max 30% para não ser muito alto

    oscillator.start(startTime);
    oscillator.stop(startTime + duration / 1000);
  }, []);

  // Tocar som baseado no tipo de evento
  const playSound = useCallback(async (type: SoundEventType) => {
    // Verificar se som está habilitado globalmente
    if (!preferences.enabled) {
      console.log(`🔇 Som desabilitado globalmente`);
      return;
    }

    // Verificar se este tipo de evento está habilitado
    if (!preferences.enabledEvents[type]) {
      console.log(`🔇 Som desabilitado para evento: ${type}`);
      return;
    }

    const ctx = getAudioContext();
    if (!ctx) return;

    // Resumir contexto se suspenso (necessário para Safari/iOS)
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch (err) {
        console.error('Erro ao resumir AudioContext:', err);
        return;
      }
    }

    const config = SOUND_CONFIGS[type];
    let currentTime = ctx.currentTime;

    // Tocar padrão de beeps
    for (let i = 0; i < config.pattern.length; i++) {
      if (i % 2 === 0) {
        // Tocar beep
        playBeep(ctx, config.frequency, config.pattern[i], config.type, preferences.volume, currentTime);
      }
      currentTime += config.pattern[i] / 1000;
    }

    console.log(`🔔 Som tocado: ${type}`);
  }, [preferences, getAudioContext, playBeep]);

  // Testar um som específico (ignora preferências)
  const testSound = useCallback(async (type: SoundEventType, volume?: number) => {
    const ctx = getAudioContext();
    if (!ctx) return;

    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch (err) {
        console.error('Erro ao resumir AudioContext:', err);
        return;
      }
    }

    const config = SOUND_CONFIGS[type];
    const testVolume = volume ?? preferences.volume;
    let currentTime = ctx.currentTime;

    for (let i = 0; i < config.pattern.length; i++) {
      if (i % 2 === 0) {
        playBeep(ctx, config.frequency, config.pattern[i], config.type, testVolume, currentTime);
      }
      currentTime += config.pattern[i] / 1000;
    }

    console.log(`🔔 Som de teste: ${type}`);
  }, [getAudioContext, playBeep, preferences.volume]);

  return {
    playSound,
    testSound,
    isEnabled: preferences.enabled,
    soundConfigs: SOUND_CONFIGS
  };
};
